import { BiConformedKey } from './catalog';
import type { BiDisplayFormat } from './format';
import { BiAggregation } from './query';
import { BiFieldType } from './result';

// Mirror of backend `models/src/bi/dataset.ts` (the wire types only) and of
// the generated enums `BiDatasetVisibility`, `BiDatasetPermission`.

export enum BiDatasetVisibility {
  Private = 1,
  Store = 2,
}

/** A grant on an uploaded dataset. */
export enum BiDatasetPermission {
  Use = 1,
  Manage = 2,
}

/** What a column of an uploaded dataset is for. */
export enum BiDatasetColumnRole {
  Dimension = 'dimension',
  Measure = 'measure',
  /** A date or datetime dimension; the primary time is one of these. */
  Time = 'time',
}

/** One column of an uploaded dataset, addressed by its stable field key. */
export interface BiDatasetColumn {
  /** Stable field key, e.g. `f_amount`; cards refer to it, never labels. */
  key: string;
  /** The label shown; the header unless the user renamed it. */
  label: string;
  /** The header in the uploaded file. */
  sourceHeader: string;
  type: BiFieldType;
  role: BiDatasetColumnRole;
  /** Measures only. */
  defaultAgg?: BiAggregation;
  /**
   * Measures only: how the column reads (spec §6.6). A `currency` column
   * is stored in cents, like every amount.
   */
  format?: BiDisplayFormat;
  conformedKey?: BiConformedKey;
}

export interface BiDatasetSchema {
  columns: BiDatasetColumn[];
  /** The field key a time range applies to. */
  primaryTime?: string;
  description: string;
}

/** What the caller may do with an uploaded dataset. */
export enum BiDatasetAccess {
  None = 'none',
  Use = 'use',
  Manage = 'manage',
}

/** One grant on an uploaded dataset. */
export interface BiDatasetGrantView {
  memberId: number;
  permission: BiDatasetPermission;
  memberName: string | null;
  createdBy: number;
  createdAt: string;
}

/** Where an uploaded dataset is used: live cards on live dashboards. */
export interface BiDatasetUsageCount {
  dashboardCount: number;
  cardCount: number;
}

/** An uploaded dataset and its current version, as the library lists it. */
export interface BiUploadedDatasetView {
  id: string;
  name: string;
  description: string | null;
  ownerMemberId: number;
  ownerName: string | null;
  visibility: BiDatasetVisibility;
  versionId: string;
  versionNo: number;
  rowCount: number;
  columnCount: number;
  /** The file the current version came from. */
  sourceFileName: string | null;
  sourceSheet: string | null;
  /** When the current version was uploaded. */
  createdAt: string;
  updatedAt: string | null;
  /** When a query last built on it; null if never. */
  lastUsedAt: string | null;
  /** What the caller may do with it: `use` or `manage`. */
  permission: BiDatasetAccess;
  usage: BiDatasetUsageCount;
}
